import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { injectIntl, intlShape } from '../../../util/reactIntl';
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  composeValidators,
  emailFormatValid,
  maxLength,
  minLength,
  required,
  requiredFieldArrayCheckbox,
  requiredStringNoTrim,
} from '../../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
  FieldCheckbox,
  FieldCheckboxGroup,
} from '../../../components';
import { hostLanguagesOptions } from '../../../util/fieldsOptions';
import arrayMutators from 'final-form-arrays';
import css from './ListingClaimForm.module.css';
import TermsAndConditions from '../../../containers/AuthenticationPage/TermsAndConditions/TermsAndConditions';

const ListingClaimFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        disabled,
        handleSubmit,
        intl,
        formId,
        invalid,
        claimUserInProgress,
        saveEmailError,
        pristine,
        values,
      } = fieldRenderProps;

      const languageOptions = [
        {
          key: 'EN',
          label: intl.formatMessage({
            id: 'Authentication.englishOption',
          }),
        },
        {
          key: 'DE',
          label: intl.formatMessage({
            id: 'Authentication.germanOption',
          }),
        },
        {
          key: 'FR',
          label: intl.formatMessage({
            id: 'Authentication.frenchOption',
          }),
        },
        {
          key: 'ES',
          label: intl.formatMessage({
            id: 'Authentication.spanishOption',
          }),
        },
        {
          key: 'PT',
          label: intl.formatMessage({
            id: 'Authentication.ptOption',
          }),
        },
        {
          key: 'IT',
          label: intl.formatMessage({
            id: 'Authentication.itOption',
          }),
        },
        {
          key: 'NL',
          label: intl.formatMessage({
            id: 'Authentication.nlOption',
          }),
        },
      ];

      const createAccountMessage = intl.formatMessage({
        id: 'ListingClaimModal.title',
      });

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = claimUserInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;

      const emailLabel = intl.formatMessage({
        id: 'ContactDetailsForm.emailLabel',
      });

      const emailPlaceholder = '';

      const emailRequiredMessage = intl.formatMessage({
        id: 'ContactDetailsForm.emailRequired',
      });
      const emailRequired = required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'ContactDetailsForm.emailInvalid',
      });
      const emailValid = emailFormatValid(emailInvalidMessage);

      const passwordLabel = intl.formatMessage({
        id: 'PasswordResetForm.passwordLabel',
      });
      const passwordPlaceholder = intl.formatMessage({
        id: 'PasswordResetForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'PasswordResetForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'PasswordResetForm.passwordTooShort',
        },
        {
          minLength: PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'PasswordResetForm.passwordTooLong',
        },
        {
          maxLength: PASSWORD_MAX_LENGTH,
        }
      );
      const passwordRequired = requiredStringNoTrim(passwordRequiredMessage);
      const passwordMinLength = minLength(passwordMinLengthMessage, PASSWORD_MIN_LENGTH);
      const passwordMaxLength = maxLength(passwordMaxLengthMessage, PASSWORD_MAX_LENGTH);

      // First name
      const firstNameLabel = intl.formatMessage({
        id: 'ProfileSettingsForm.firstNameLabel',
      });
      const firstNamePlaceholder = intl.formatMessage({
        id: 'ProfileSettingsForm.firstNamePlaceholder',
      });
      const firstNameRequiredMessage = intl.formatMessage({
        id: 'ProfileSettingsForm.firstNameRequired',
      });
      const firstNameRequired = required(firstNameRequiredMessage);

      // Last name
      const lastNameLabel = intl.formatMessage({
        id: 'ProfileSettingsForm.lastNameLabel',
      });
      const lastNamePlaceholder = intl.formatMessage({
        id: 'ProfileSettingsForm.lastNamePlaceholder',
      });
      const lastNameRequiredMessage = intl.formatMessage({
        id: 'ProfileSettingsForm.lastNameRequired',
      });
      const lastNameRequired = required(lastNameRequiredMessage);

      const privacyLabel = intl.formatMessage({
        id: 'ClaimListingPanel.privacyLabel',
      });

      const termsLink = (
        <a className={css.termsLink} target="_blank" href="/terms-of-service">
          {intl.formatMessage({
            id: 'AuthenticationPage.termsAndConditionsTermsLinkText',
          })}
        </a>
      );

      const privacyLink = (
        <a className={css.privacyLink} target="_blank" href="/data-protection">
          {intl.formatMessage({
            id: 'AuthenticationPage.termsAndConditionsPrivacyLinkText',
          })}
        </a>
      );

      const emailTakenErrorText =
        saveEmailError?.type === 'error'
          ? intl.formatMessage({ id: 'ContactDetailsForm.emailTakenError' })
          : null;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.nameContainer}>
            <FieldTextInput
              className={css.firstName}
              type="text"
              id="firstName"
              name="firstName"
              label={firstNameLabel}
              placeholder={firstNamePlaceholder}
              validate={firstNameRequired}
            />
            <FieldTextInput
              className={css.lastName}
              type="text"
              id="lastName"
              name="lastName"
              label={lastNameLabel}
              placeholder={lastNamePlaceholder}
              validate={lastNameRequired}
            />
          </div>
          <FieldTextInput
            type="email"
            name="email"
            className={css.email}
            id={formId ? `${formId}.email` : 'email'}
            label={emailLabel}
            placeholder={emailPlaceholder}
            validate={composeValidators(emailRequired, emailValid)}
          />

          <FieldTextInput
            className={css.password}
            type="password"
            id={formId ? `${formId}.password` : 'password'}
            name="password"
            autoComplete="new-password"
            label={passwordLabel}
            placeholder={passwordPlaceholder}
            validate={composeValidators(passwordRequired, passwordMinLength, passwordMaxLength)}
          />
          <FieldSelect
            className={css.password}
            id="preferredLanguage"
            name="preferredLanguage"
            label={intl.formatMessage({
              id: 'Authentication.preferredLanguageLabel',
            })}
            subLabel={intl.formatMessage({
              id: 'Authentication.preferredLanguageLabelHelper',
            })}
            validate={required(
              intl.formatMessage({
                id: 'Authentication.preferredLanguageRequired',
              })
            )}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' })}
            </option>
            {languageOptions.map(l => (
              <option key={l?.key} value={l?.key}>
                {l?.label}
              </option>
            ))}
          </FieldSelect>

          <div className={classNames(css.newsletter, css.password)}>
            <div className={css.newsletterText}>
              {intl.formatMessage({ id: 'Authentication.newsletterHeading' })}
            </div>
            <FieldCheckbox
              id="newsletter"
              className={css.checkboxGroupField}
              name="newsletter"
              label={intl.formatMessage({ id: 'Authentication.newsletter' })}
              value="true"
            />
            <FieldCheckbox
              id="terms"
              className={css.checkboxGroupField}
              name="terms"
              label={intl.formatMessage(
                { id: 'AuthenticationPage.termsAndConditionsAcceptText' },
                { termsLink, privacyLink }
              )}
              value="true"
              validate={requiredFieldArrayCheckbox(
                intl.formatMessage({ id: 'AuthenticationPage.termsAndConditionsAcceptRequired' })
              )}
            />
          </div>
          <div className={css.error}>{emailTakenErrorText}</div>
          <PrimaryButton
            className={css.submitButton}
            type="submit"
            inProgress={claimUserInProgress}
            disabled={submitDisabled}
          >
            {createAccountMessage}
          </PrimaryButton>
        </Form>
      );
    }}
  />
);

ListingClaimFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  sendReviewError: null,
};

const { bool, func, string } = PropTypes;

ListingClaimFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
};

const ListingClaimForm = compose(injectIntl)(ListingClaimFormComponent);
ListingClaimForm.displayName = 'ListingClaimForm';

export default ListingClaimForm;
